import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import FlashReportBanner from "@/components/FlashReportBanner";
import { Users, ShieldCheck, Crosshair, Briefcase } from "lucide-react";

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="border border-border p-6 bg-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="meta-label">{label}</div>
        <Icon size={16} className="text-muted-foreground" />
      </div>
      <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stats");
        setStats(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  return (
    <div className="font-body">
      <PageHeader
        eyebrow="Overview"
        title="Lead-Ops Dashboard"
        description="Real-time signal on your leads, campaigns and exclusion rules."
        testid="dashboard-header"
      />
      <FlashReportBanner />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Total Leads" value={stats?.total_leads ?? "—"} icon={Users} testid="stat-total-leads" />
          <Stat label="Campaigns" value={stats?.total_campaigns ?? "—"} icon={Briefcase} testid="stat-total-campaigns" />
          <Stat label="Agents" value={stats?.total_agents ?? "—"} icon={Users} testid="stat-total-agents" />
          <Stat label="Status" value="Online" icon={ShieldCheck} testid="stat-status" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Link to="/master-exclusion" className="border border-border p-6 hover:bg-secondary transition-colors group">
            <div className="flex items-center justify-between mb-3">
              <ShieldCheck className="text-primary" size={18} />
              <span className="meta-label">Tool</span>
            </div>
            <h3 className="font-display text-xl font-bold">Master Exclusion Check</h3>
            <p className="text-sm text-muted-foreground mt-2">Search the entire database by email or domain.</p>
            <div className="text-xs font-mono text-primary mt-4 group-hover:underline">Run check →</div>
          </Link>
          <Link to="/campaign-exclusion" className="border border-border p-6 hover:bg-secondary transition-colors group">
            <div className="flex items-center justify-between mb-3">
              <Crosshair className="text-primary" size={18} />
              <span className="meta-label">Tool</span>
            </div>
            <h3 className="font-display text-xl font-bold">Campaign Exclusion Check</h3>
            <p className="text-sm text-muted-foreground mt-2">Scope to one or many campaigns and validate.</p>
            <div className="text-xs font-mono text-primary mt-4 group-hover:underline">Run check →</div>
          </Link>
          <Link to="/bulk-upload" className="border border-border p-6 hover:bg-secondary transition-colors group">
            <div className="flex items-center justify-between mb-3">
              <Users className="text-primary" size={18} />
              <span className="meta-label">Action</span>
            </div>
            <h3 className="font-display text-xl font-bold">Bulk Upload</h3>
            <p className="text-sm text-muted-foreground mt-2">CSV import using the same form-field template.</p>
            <div className="text-xs font-mono text-primary mt-4 group-hover:underline">Upload now →</div>
          </Link>
        </div>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-border lg:col-span-2">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="meta-label">Recent</div>
                <h3 className="font-display text-lg font-bold">Latest Leads</h3>
              </div>
              <Link to="/leads" className="text-xs font-mono text-primary hover:underline">View all →</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border bg-secondary/40">
                    <th className="px-4 py-2 meta-label">Lead ID</th>
                    <th className="px-4 py-2 meta-label">Name</th>
                    <th className="px-4 py-2 meta-label">Email</th>
                    <th className="px-4 py-2 meta-label">Campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.latest_leads ?? []).length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No leads yet. Upload a CSV or add one.</td></tr>
                  )}
                  {(stats?.latest_leads ?? []).map((l) => (
                    <tr key={l.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{l.lead_id || l.id.slice(0,8)}</td>
                      <td className="px-4 py-2">{l.first_name} {l.last_name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{l.work_email}</td>
                      <td className="px-4 py-2">{l.campaign_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="border border-border">
            <div className="px-6 py-4 border-b border-border">
              <div className="meta-label">Distribution</div>
              <h3 className="font-display text-lg font-bold">Leads by Campaign</h3>
            </div>
            <div className="p-6 space-y-3">
              {(stats?.by_campaign ?? []).length === 0 && (
                <div className="text-sm text-muted-foreground">No data yet.</div>
              )}
              {(stats?.by_campaign ?? []).map((c) => (
                <div key={c.campaign}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="truncate">{c.campaign}</span>
                    <span className="font-mono">{c.count}</span>
                  </div>
                  <div className="h-1.5 bg-secondary">
                    <div className="h-full bg-primary" style={{ width: `${Math.min(100, c.count * 4)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
